import * as React from "react";
import {
    Mention20Regular,
    ArrowClockwise20Regular,
    Dismiss20Regular,
} from "@fluentui/react-icons";
import type { MailMentionItemDto } from "../../services/mailNoteService";
import { groupMentionsByDate } from "./mailMentions";
import { mailboxChipLabel } from "./mailSearch";
import { AUB, INK, INK2, LINE, BG_SOFT, GOLD, ACTIVE_BG } from "../chat/chatTheme";

/**
 * SP8 — @-Erwähnungen-Ansicht (Mail-Topbar-Toggle, A6).
 *
 * Gefilterte Liste der Mails, in denen mich jemand über eine „Mail besprechen"-
 * Notiz erwähnt hat (`GET /mail/notes/mentions/me`, Entscheidung 24: nur
 * KMVS-Mentions). Chronologisch nach Datum gruppiert, ungelesene hervorgehoben;
 * Klick öffnet die Mail (auch aus fremdem, lesbarem Postfach) und markiert die
 * Erwähnung als gelesen. Rein präsentational — Daten/State liegen in `MailView`.
 */

type MailMentionsViewProps = {
    mentions: MailMentionItemDto[];
    loading: boolean;
    onOpen: (m: MailMentionItemDto) => void;
    onRefresh: () => void;
    onClose: () => void;
};

const fmtTime = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export const MailMentionsView: React.FC<MailMentionsViewProps> = ({
    mentions,
    loading,
    onOpen,
    onRefresh,
    onClose,
}) => {
    const groups = React.useMemo(() => groupMentionsByDate(mentions), [mentions]);

    const iconBtn: React.CSSProperties = {
        border: "none",
        background: "transparent",
        cursor: "pointer",
        color: INK2,
        display: "inline-flex",
        alignItems: "center",
        padding: 4,
        borderRadius: 6,
    };

    const rowStyle = (unread: boolean): React.CSSProperties => ({
        display: "flex",
        flexDirection: "column",
        gap: 3,
        padding: "10px 12px",
        borderRadius: 8,
        border: `1px solid ${LINE}`,
        borderLeft: unread ? `3px solid ${GOLD}` : `1px solid ${LINE}`,
        marginBottom: 8,
        cursor: "pointer",
        background: unread ? ACTIVE_BG : "#fff",
    });

    const chipStyle: React.CSSProperties = {
        display: "inline-block",
        padding: "0 6px",
        borderRadius: 8,
        backgroundColor: ACTIVE_BG,
        color: AUB,
        fontSize: 10.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
    };

    const groupLabelStyle: React.CSSProperties = {
        fontSize: 12,
        fontWeight: 700,
        color: INK2,
        padding: "8px 2px 4px",
        letterSpacing: "0.2px",
    };

    const hintStyle: React.CSSProperties = {
        color: INK2,
        fontSize: 13,
        textAlign: "center",
        padding: "40px 16px",
        lineHeight: 1.6,
    };

    const renderBody = () => {
        if (loading && mentions.length === 0) return <div style={hintStyle}>Lädt …</div>;
        if (mentions.length === 0) {
            return (
                <div style={hintStyle}>
                    <Mention20Regular style={{ fontSize: 28, color: INK2, opacity: 0.7 }} />
                    <div style={{ marginTop: 10, fontWeight: 600, color: INK }}>
                        Keine Erwähnungen
                    </div>
                    <div style={{ marginTop: 4 }}>
                        Wirst du in einer „Mail besprechen"-Notiz mit @ erwähnt, erscheint die
                        Mail hier.
                    </div>
                </div>
            );
        }
        return (
            <>
                {groups.map((g) => (
                    <div key={g.label}>
                        <div style={groupLabelStyle}>{g.label}</div>
                        {g.items.map((m) => (
                            <div
                                key={m.noteId}
                                style={rowStyle(!m.read)}
                                role="button"
                                tabIndex={0}
                                onClick={() => onOpen(m)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onOpen(m);
                                    }
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                                        {!m.read && (
                                            <span
                                                style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: GOLD, flexShrink: 0 }}
                                                aria-label="ungelesen"
                                            />
                                        )}
                                        <span style={{ fontWeight: m.read ? 600 : 700, fontSize: 13, color: INK, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {m.subject || "Kein Betreff"}
                                        </span>
                                    </span>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                        <span style={chipStyle}>{mailboxChipLabel(m.mailbox)}</span>
                                        <span style={{ fontSize: 11, color: INK2, whiteSpace: "nowrap" }}>{fmtTime(m.timestamp)}</span>
                                    </span>
                                </div>
                                <div style={{ fontSize: 12.5, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    <span style={{ color: AUB, fontWeight: 600 }}>{m.author}</span>
                                    {": "}
                                    {m.text || "(ohne Text)"}
                                </div>
                                {m.senderName && (
                                    <div style={{ fontSize: 11.5, color: INK2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        Von: {m.senderName}
                                        {m.senderEmail ? ` ‹${m.senderEmail.toLowerCase()}›` : ""}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </>
        );
    };

    return (
        <div
            style={{
                flexGrow: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "10px 14px",
                    borderBottom: `1px solid ${LINE}`,
                    backgroundColor: BG_SOFT,
                    flexShrink: 0,
                }}
            >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <Mention20Regular style={{ color: AUB, flexShrink: 0 }} />
                    <span style={{ fontWeight: 700, fontSize: 14, color: AUB }}>Erwähnungen</span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                    <button type="button" style={iconBtn} title="Aktualisieren" aria-label="Erwähnungen aktualisieren" onClick={onRefresh}>
                        <ArrowClockwise20Regular />
                    </button>
                    <button type="button" style={iconBtn} title="Schließen" aria-label="Erwähnungen schließen" onClick={onClose}>
                        <Dismiss20Regular />
                    </button>
                </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "10px 14px", maxWidth: 720, width: "100%", margin: "0 auto" }}>
                {renderBody()}
            </div>
        </div>
    );
};
